import { motion } from "framer-motion";

export function Reveal({ children, delay = 0, y = 28, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function MaskedLines({ lines, className = "", lineClassName = "", delay = 0.15 }) {
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-1">
          <motion.span
            className={`block ${lineClassName}`}
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay: delay + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export function Eyebrow({ children, dark = false }) {
  return (
    <p className={`eyebrow ${dark ? "text-gold" : "text-gold-dark"}`} data-testid="section-eyebrow">
      {children}
    </p>
  );
}

export function SectionHead({ eyebrow, title, description, dark = false, align = "left" }) {
  return (
    <Reveal className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      {eyebrow && <Eyebrow dark={dark}>{eyebrow}</Eyebrow>}
      <h2 className={`mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08] ${dark ? "text-parchment" : "text-forest"}`}>
        {title}
      </h2>
      {description && (
        <p className={`mt-5 text-base sm:text-lg leading-relaxed ${dark ? "text-[#9EB8A8]" : "text-[#4A554D]"}`}>{description}</p>
      )}
    </Reveal>
  );
}

export function Marquee({ items }) {
  const row = [...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-white/10 bg-forest-deep py-5" data-testid="editorial-marquee">
      <div className="flex w-max animate-marquee gap-0">
        {row.map((item, i) => (
          <span key={i} className="flex items-center whitespace-nowrap">
            <span className="font-serif text-2xl sm:text-3xl italic text-parchment/80 px-6">{item}</span>
            <span className="h-1.5 w-1.5 rounded-full bg-gold/70" />
          </span>
        ))}
      </div>
    </div>
  );
}

export function PageHero({ eyebrow, title, description, image }) {
  return (
    <section className="relative overflow-hidden bg-forest grain" data-testid="page-hero">
      {image && (
        <div className="absolute inset-0">
          <img src={image} alt="" className="h-full w-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-forest-deep/70 via-forest/80 to-forest" />
        </div>
      )}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 pt-36 pb-20 sm:pt-44 sm:pb-24">
        <Reveal>
          <Eyebrow dark>{eyebrow}</Eyebrow>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.06] text-parchment max-w-3xl">
            {title}
          </h1>
          {description && <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-[#9EB8A8]">{description}</p>}
        </Reveal>
      </div>
    </section>
  );
}
